import React from 'react';
import { generatePath, Route } from 'react-router';

import { MDText } from 'i18n-react';

import { type Document, DOCUMENT_UPLOAD_EXIT_PATHS, DOCUMENT_UPLOAD_TYPE } from '../DocumentCapture.helpers';
import type { DocumentCaptureRouteNamesValue, DocumentKindsValue } from '../DocumentCapture.types';

import { documentNeedsReupload } from './UploadDocuments.selectors';
import LocalTexts from './UploadDocuments.texts.json';
import UploadDocumentsPage from './UploadDocumentsPage';
import { UploadDocumentSections } from './UploadDocumentsSections';

const LocalT = new MDText(LocalTexts);

export const resolveDocumentTileReactivation = (document?: Document) => {
  if (document && document.images) {
    const hasImagesNeedingReupload = Object.values(document.images)
      .some((image) => documentNeedsReupload(image));

    return hasImagesNeedingReupload;
  }

  return false;
};

export const getDocumentUploadPath = ({ kind, path }: { kind?: DocumentCaptureRouteNamesValue; path: string } = { path: '/' }) => generatePath(path, { kind });

export const getDocumentUploadRoutes = (path: string, documents?: Document[]) => {
  const documentKindsRoutes: {
    [documentType in DocumentKindsValue]?: React.ReactElement
  } = Object.entries(UploadDocumentSections).reduce(
    (previousValue, currentValue) => {
      const [kind, { route: { name } }] = currentValue;
      const currentDocument = documents?.find((doc) => doc.kind === kind);
      // We shouldnt be adding routes if the document wasn't passed to the hook.
      if (!currentDocument) {
        return previousValue;
      }
      const isReactivated = resolveDocumentTileReactivation(currentDocument);

      const UploadRoute = <Route
        key={kind}
        exact
        // @ts-expect-error This is not an official prop but it is required for Wizard.
        name={name}
        path={getDocumentUploadPath({ kind: name, path })}
      >
        {/* ts-expect-error UploadDocuments need to be converted */}
        <UploadDocumentsPage
          document={currentDocument}
          kind={kind as DocumentKindsValue}
          reactivated={isReactivated}
          uploadDocType={DOCUMENT_UPLOAD_TYPE.DEFAULT}
        />
      </Route>;

      return {
        ...previousValue,
        [kind]: UploadRoute,
      };
    },
    {},
  );

  const {
    additional_documents: ADDITIONAL_DOCUMENTS_ROUTE,
    bank_statement: BANK_STATEMENT_ROUTE,
    death_certificate: DEATH_CERTIFICATE_ROUTE,
    driving_licence: DRIVING_LICENCE_ROUTE,
    finance_settlement_letter: FINANCE_SETTLEMENT_LETTER_ROUTE,
    letter_of_consent: LETTER_OF_CONSENT_ROUTE,
    passport: PASSPORT_ROUTE,
    proof_of_purchase: PROOF_OF_PURCHASE_ROUTE,
    solicitors_letter: SOLICITORS_LETTER_ROUTE,
    utility_bill: PROOF_OF_ADDRESS_ROUTE,
    v5: V5_ROUTE,
    will: WILL_ROUTE,
  } = documentKindsRoutes;

  return {
    ADDITIONAL_DOCUMENTS_ROUTE,
    BANK_STATEMENT_ROUTE,
    DEATH_CERTIFICATE_ROUTE,
    DRIVING_LICENCE_ROUTE,
    FINANCE_SETTLEMENT_LETTER_ROUTE,
    LETTER_OF_CONSENT_ROUTE,
    PASSPORT_ROUTE,
    PROOF_OF_ADDRESS_ROUTE,
    PROOF_OF_PURCHASE_ROUTE,
    SOLICITORS_LETTER_ROUTE,
    V5_ROUTE,
    WILL_ROUTE,
  };
};

export const getExitPath = (uploadDocType: DOCUMENT_UPLOAD_TYPE) => {
  if (uploadDocType && DOCUMENT_UPLOAD_EXIT_PATHS[uploadDocType]) {
    return DOCUMENT_UPLOAD_EXIT_PATHS[uploadDocType];
  }

  return DOCUMENT_UPLOAD_EXIT_PATHS.default;
};

export const getFinanceErrorMessage = (error: string) => {
  switch (error) {
  case 'provider-service-down':
    return {
      content: LocalT.translate('financePartnerServiceDown.content'),
      heading: LocalT.translate('financePartnerServiceDown.title'),
    };
  case 'agreement-not-found':
  default:
    return {
      content: LocalT.translate('financeAgreementNotFound.content'),
      heading: LocalT.translate('financeAgreementNotFound.title'),
    };
  }
};
